import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { usePricingAccess } from '@/hooks/usePricingAccess';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Building2, Users, Plus } from 'lucide-react';

const Onboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const { tenant, isLoading: pricingLoading } = usePricingAccess();
  const { hasCompletedSetup, isLoading: preferencesLoading } = useUserPreferences();
  const [formData, setFormData] = useState({
    tenantName: '',
    joinCode: '',
    firstName: '',
    lastName: ''
  });

  // Simplified redirect logic using user preferences
  useEffect(() => {
    if (!user || loading || preferencesLoading || pricingLoading) return;

    // If user has completed onboarding, go to dashboard
    if (hasCompletedSetup && tenant) {
      navigate('/dashboard');
    } else if (tenant) {
      navigate('/quick-setup');
    }
  }, [user, loading, hasCompletedSetup, preferencesLoading, pricingLoading, tenant, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const createTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      // Create tenant and assign owner role atomically
      const { data: tenantId, error: tenantError } = await supabase.rpc(
        'create_tenant_with_owner',
        { tenant_name: formData.tenantName }
      );

      if (tenantError) throw tenantError;

      // Update user profile
      const metaName =
        (user.user_metadata?.full_name as string) ||
        (user.user_metadata?.name as string) ||
        '';
      await supabase
        .from('users_public')
        .upsert({
          id: user.id,
          name: `${formData.firstName} ${formData.lastName}`.trim() || metaName || user.email?.split('@')[0] || 'User'
        });


      // Refresh the cached subscription state (it may still hold the old
      // "no business" answer) before leaving this page.
      await queryClient.invalidateQueries({ queryKey: ['subscription-state'] });

      toast({
        title: "Organization created!",
        description: "Welcome to Crystal HACCP. Let's complete your setup.",
      });

      // Redirect to simplified quick setup
      navigate('/quick-setup');
    } catch (error: any) {
      toast({
        title: "Error creating organization",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const joinTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      // Join tenant by name and assign staff role atomically
      const { data: tenantId, error: tenantError } = await supabase.rpc(
        'join_tenant_by_name',
        { tenant_name: formData.joinCode }
      );

      if (tenantError) throw tenantError;

      // Update user profile
      await supabase
        .from('users_public')
        .upsert({
          id: user.id,
          name: `${formData.firstName} ${formData.lastName}`.trim() || user.email?.split('@')[0] || 'User'
        });

      // Mark setup as complete in user preferences
      await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          setup_completed: true,
        });

      // Refresh the cached subscription state (it may still hold the old
      // "no business" answer) before heading to the dashboard.
      await queryClient.invalidateQueries({ queryKey: ['subscription-state'] });

      toast({
        title: "Joined organization!",
        description: `Welcome to the organization`,
      });

      navigate('/dashboard');
    } catch (error: any) {
      toast({
        title: "Error joining organization",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-accent/20 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-crystal-white mb-2">Welcome to Crystal HACCP</h1>
          <p className="text-muted-foreground">
            Let's get your food safety management system set up
          </p>
        </div>

        <Card className="glass-panel">
          <CardHeader className="text-center">
            <CardTitle>Get Started</CardTitle>
            <CardDescription>
              Create a new organization or join an existing one
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="create" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="create">Create Organization</TabsTrigger>
                <TabsTrigger value="join">Join Organization</TabsTrigger>
              </TabsList>

              <TabsContent value="create">
                <form onSubmit={createTenant} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="tenantName">What's your business called?</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="tenantName"
                        name="tenantName"
                        type="text"
                        placeholder="e.g. Harbour Street Kitchen"
                        value={formData.tenantName}
                        onChange={handleInputChange}
                        className="glass-input pl-10"
                        autoFocus
                        required
                        minLength={3}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      7-day free trial. No card needed. You can change the name later.
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full cta-primary" 
                    disabled={loading || formData.tenantName.trim().length < 3}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Creating...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Start my free trial
                      </div>
                    )}
                  </Button>
                </form>

              </TabsContent>

              <TabsContent value="join">
                <form onSubmit={joinTenant} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        type="text"
                        placeholder="John"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="glass-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        type="text"
                        placeholder="Doe"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="glass-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="joinCode">Organization Name</Label>
                    <div className="relative">
                      <Users className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="joinCode"
                        name="joinCode"
                        type="text"
                        placeholder="Organization name to join"
                        value={formData.joinCode}
                        onChange={handleInputChange}
                        className="glass-input pl-10"
                        required
                        minLength={3}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Enter the exact organization name provided by your administrator
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full cta-secondary" 
                    disabled={loading || !formData.joinCode.trim() || !formData.firstName.trim()}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Joining...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Join Organization
                      </div>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Onboarding;